import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/lib/Modal';
import Highlighter from 'react-highlight-words';
import Toggle from 'react-toggle';
import classnames from 'classnames';
import { injectIntl, FormattedMessage } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';
import Button from '@/shared/components/Button';
import api from '@/shared/api';
import config from '@/config';
import handleHttpError from '@/shared/services/HandleHttpError';
import messages from './assignmentTemplateMessages';
import guid from '@/shared/utils/guid';
import authService from '@/shared/services/AuthService';
import ToastrService from '@/shared/services/ToastrService';
import { mapResourcesToTree } from '@/shared/hooks/resource/useResourceTree';
import LoadingContainer from '@/shared/components/loading/LoadingContainer';
import TextSearch from '@/shared/components/input/TextSearch';
import Checkbox from '@/shared/components/input/Checkbox';
import { resourceSearch } from '@/shared/components/registration/ResourceAutoComplete';
import ZendeskHelp from '@/shared/components/ZendeskHelp';

class EditAssignmentTemplateResourcesModal extends Component {
  constructor(props) {
    super(props);
    const { assignmentTemplateId, assignmentTemplateName, intl } = props;
    this.state = {
      templateName:
        assignmentTemplateId === guid.empty
          ? intl.formatMessage(messages.DefaultAssignment)
          : assignmentTemplateName,
      saving: false,
      readOnly: !authService.hasPermission({
        functionality: 'Settings',
        privilege: 'Update',
      }),
      resources: null,
      allBlocked: false,
      updateRegistrations: true,
      searchValue: '',
    };
  }

  componentDidMount() {
    const { intl, assignmentTemplateId } = this.props;
    api.base
      .get(`${config.appUrls.api}/api/AssignmentTemplate/Resources/${assignmentTemplateId}`)
      .then(({ data }) => {
        const resources = mapResourcesToTree(
          data.filter(x => x.type !== 3 && x.type !== 4),
          intl,
        );
        const allBlocked = resources.every(r => r.blocked);
        this.setState({ resources, allBlocked });
      });
  }

  save = () => {
    const { saving, resources, updateRegistrations } = this.state;
    const { onClose, assignmentTemplateId } = this.props;
    if (saving) return '';

    this.setState({ saving: true });

    const model = {
      assignmentTemplateId,
      resources: resources.filter(x => x.blocked || x.inForfait !== null),
      updateRegistrations,
    };

    return api.base
      .put(`${config.appUrls.api}/api/AssignmentTemplate/Resources`, model)
      .then(
        () => {
          ToastrService.success();
          onClose();
        },
        error => {
          const { intl } = this.props;
          handleHttpError(intl)(error);
          return error;
        },
      )
      .finally(() => {
        if (typeof this !== 'undefined') this.setState({ saving: false });
      });
  };
  setBlocked = (resource, blocked) => {
    this.setState(({ resources }) => ({
      resources: resources.map(x => (x.id === resource.id ? { ...x, blocked } : x)),
    }));
  };
  setAllBlocked = blocked => {
    this.setState(({ resources, searchValue }) => ({
      resources: resources.map(x =>
        !searchValue || resourceSearch(x, searchValue) ? { ...x, blocked } : x,
      ),
    }));
  };
  setInForfait = (resource, inForfait) => {
    this.setState(({ resources }) => ({
      resources: resources.map(x => (x.id === resource.id ? { ...x, inForfait } : x)),
    }));
  };
  setAllInForfait = inForfait => {
    this.setState(({ resources, searchValue }) => ({
      resources: resources.map(x =>
        !searchValue || resourceSearch(x, searchValue) ? { ...x, inForfait } : x,
      ),
    }));
  };

  render() {
    const { onClose, intl, taskTemplates } = this.props;
    const {
      saving,
      templateName,
      readOnly,
      resources,
      searchValue,
      updateRegistrations,
    } = this.state;

    const filteredResources =
      searchValue && resources ? resources.filter(x => resourceSearch(x, searchValue)) : resources;
    const allAllowed = filteredResources?.every(x => !x.blocked) ?? false;
    const allInForfait = filteredResources?.every(x => !!x.inForfait) ?? false;
    const hasInForfaitOverriden = resources?.some(x => x.inForfait !== null) ?? false;

    return (
      <Modal backdrop="static" show onHide={() => onClose()} keyboard={false} bsSize="large">
        <Modal.Header closeButton>
          <Modal.Title>
            <FormattedMessage {...messages.ConfigureAllowResources} />
            &nbsp;-&nbsp;{templateName}
            <ZendeskHelp article="5434869059730" anchor="#h_01J3Z6WPZQ4DX8EJ6T1RVWBSZ2" />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="scrollable">
          <LoadingContainer isLoading={!resources}>
            <div className="form-horizontal">
              <div className="form-group">
                <div className="col-sm-12">
                  <TextSearch
                    className="form-control"
                    value={searchValue}
                    placeholder={intl.formatMessage(messages.Search)}
                    onChange={value => this.setState({ searchValue: value || '' })}
                  />
                </div>
              </div>
              {hasInForfaitOverriden ? (
                <div className="alert alert-warning" role="alert">
                  <FormattedMessage {...messages.InForfaitInheritanceInfo} />
                </div>
              ) : null}
              <table className="width100pct table-hover margin10T">
                <thead>
                  <tr>
                    <th colSpan={2}>{intl.formatMessage(messages.Name)}</th>
                    <th>
                      <Checkbox
                        value={allAllowed}
                        readOnly={readOnly}
                        onClick={checked => this.setAllBlocked(!checked)}
                      />{' '}
                      {intl.formatMessage(messages.Allowed)}
                      {!readOnly && searchValue ? (
                        <span> ({intl.formatMessage(messages.OnlyFiltered)})</span>
                      ) : null}
                    </th>
                    <th>
                      <Checkbox
                        value={allInForfait}
                        readOnly={readOnly}
                        onClick={checked => this.setAllInForfait(checked)}
                      />{' '}
                      {intl.formatMessage(messages.InForfait)}
                      {!readOnly && (
                        <>
                          <a
                            onClick={() => this.setAllInForfait(null)}
                            className={classnames('margin5L', {
                              invisible: !hasInForfaitOverriden,
                            })}
                          >
                            <i
                              className="fa fa-undo fa-fw"
                              title={intl.formatMessage(messages.ResetInForfaitForAll)}
                            ></i>{' '}
                          </a>
                          {searchValue ? (
                            <span> ({intl.formatMessage(messages.OnlyFiltered)})</span>
                          ) : null}
                        </>
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredResources ? (
                    filteredResources.map(x => (
                      <tr key={x.id}>
                        {x.level === 1 ? <td>&nbsp;</td> : null}
                        <td colSpan={x.level === 1 ? 1 : 2}>
                          {searchValue ? (
                            <Highlighter
                              highlightClassName="bold"
                              searchWords={[searchValue]}
                              textToHighlight={x.label}
                            />
                          ) : (
                            x.label
                          )}
                        </td>
                        <td>
                          <Checkbox
                            value={!x.blocked}
                            readOnly={readOnly}
                            onClick={checked => this.setBlocked(x, !checked)}
                          />
                        </td>
                        <td>
                          <Checkbox
                            value={x.inForfait ?? x.resourceInForfait}
                            readOnly={readOnly}
                            extraClass={classnames({ 'text-orange': x.inForfait !== null })}
                            onClick={checked => this.setInForfait(x, checked)}
                          />
                          {x.inForfait !== null && !readOnly ? (
                            <a onClick={() => this.setInForfait(x, null)} className="margin5L">
                              <i
                                className="fa fa-undo fa-fw"
                                title={intl.formatMessage(
                                  messages.InForfaitSetForAssignmentTemplateClickToReset,
                                )}
                              ></i>
                            </a>
                          ) : null}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4}>
                        <FormattedMessage {...messages.NoItems} />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </LoadingContainer>
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={saving} onClick={() => onClose()}>
            <FormattedMessage {...(readOnly ? messages.Close : messages.Cancel)} />
          </Button>
          {!readOnly ? (
            <>
              <Button
                onClick={() => this.save()}
                disabled={saving}
                btnClassName="btn-inverse"
                showProgress
              >
                <FormattedMessage {...messages.Save} />
              </Button>
              <label className="react-toggle-wrapper margin5T pull-left">
                <Toggle
                  checked={updateRegistrations}
                  onChange={e => this.setState({ updateRegistrations: e.target.checked })}
                />
                <FormattedMessage
                  className="label-text"
                  {...messages.RecalculateInForfaitOnRegistrations}
                />
              </label>
            </>
          ) : null}
        </Modal.Footer>
      </Modal>
    );
  }
}

EditAssignmentTemplateResourcesModal.propTypes = {
  intl: intlShape.isRequired,
  onClose: PropTypes.func.isRequired,
  assignmentTemplateId: PropTypes.string.isRequired,
  assignmentTemplateName: PropTypes.string,
};

export default injectIntl(EditAssignmentTemplateResourcesModal);
